import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { UserPlus, RefreshCw, CheckCircle2, XCircle, MessageSquare, ExternalLink, AlertTriangle, Copy, Ban } from "lucide-react";
import { Navbar } from "../components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer-registration`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const STATUS_COLORS = {
  under_review: "bg-indigo-100 text-indigo-700",
  more_information_requested: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  incomplete: "bg-slate-100 text-slate-600",
  email_verification_pending: "bg-slate-100 text-slate-500",
  archived: "bg-slate-200 text-slate-500",
};

const SECTION_LABELS = {
  company: "Company",
  addresses: "Addresses",
  operations_contact: "Operations Contact",
  finance_contacts: "Finance Contacts",
  banking: "Banking",
  declarations: "Declarations",
};

export default function CustomerApprovalsPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");
  const [mergeReason, setMergeReason] = useState("Duplicate registration");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    const r = await axios.get(`${API}?${params.toString()}`, auth());
    setItems(r.data.items || []);
    setCounts(r.data.status_counts || {});
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    const r = await axios.get(`${API}/${id}`, auth());
    setSel(r.data);
  };

  const act = async (path, body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/${sel.id}/${path}`, body, auth());
      await open(sel.id); await load();
    } catch (e) { alert(e.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };

  const prettify = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const doCancel = () => {
    const reason = prompt("Cancel this registration — reason (e.g. duplicate, withdrawn):", "Duplicate registration");
    if (reason && reason.trim()) act("cancel", { reason: reason.trim() });
  };

  const doMerge = async () => {
    if (!mergeTarget) { alert("Pick a record to merge into"); return; }
    await act("merge", { target_id: mergeTarget, reason: mergeReason || "Duplicate" });
    setMergeOpen(false); setMergeTarget(""); setSel(null);
  };
  const KV = ({ obj }) => (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 px-3 pb-3 sm:grid-cols-2">
      {Object.entries(obj).filter(([, v]) => v !== null && v !== "" && v !== undefined && typeof v !== "object").map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 border-b border-slate-50 py-1 text-xs">
          <dt className="text-slate-400">{prettify(k)}</dt>
          <dd className="text-right font-medium text-slate-700">{String(v)}</dd>
        </div>
      ))}
    </dl>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="mx-auto max-w-6xl px-6 py-6 pt-24">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-600 p-2 text-white"><UserPlus size={22} /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Customer Approvals</h1>
              <p className="text-sm text-slate-500">New customer registration (KYC) review & approval</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/customer-registration" target="_blank" rel="noreferrer"
              className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-600">
              <ExternalLink size={14} /> Public form
            </a>
            <button data-testid="customer-refresh" onClick={load} className="flex items-center gap-1 rounded-lg bg-slate-800 px-3 py-2 text-sm text-white">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          <button onClick={() => setStatus("")} className={`rounded-full px-3 py-1 text-xs ${status === "" ? "bg-blue-600 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>All</button>
          {Object.entries(counts).map(([s, n]) => (
            <button key={s} data-testid={`customer-filter-${s}`} onClick={() => setStatus(s)}
              className={`rounded-full px-3 py-1 text-xs ${status === s ? "bg-blue-600 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
              {s.replace(/_/g, " ")} ({n})
            </button>
          ))}
          <input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company / email"
            className="ml-auto rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="lg:col-span-2 space-y-2">
            {items.length === 0 && <div className="rounded-xl bg-white p-6 text-center text-sm text-slate-400">No registrations</div>}
            {items.map((it) => (
              <button key={it.id} data-testid={`customer-row-${it.id}`} onClick={() => open(it.id)}
                className={`w-full rounded-xl border bg-white p-3 text-left transition hover:shadow-sm ${sel?.id === it.id ? "border-blue-400" : "border-slate-200"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{it.company?.registered_name || it.email}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_COLORS[it.status] || "bg-slate-100 text-slate-600"}`}>{it.status?.replace(/_/g, " ")}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{it.company?.vat_number || "no VAT"} · {it.email}</div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-3">
            {!sel ? (
              <div className="rounded-xl bg-white p-8 text-center text-sm text-slate-400">Select a registration to review</div>
            ) : (
              <div className="rounded-xl bg-white p-5 shadow-sm" data-testid="customer-detail">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-slate-800">{sel.company?.registered_name || sel.email}</h2>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[sel.status] || "bg-slate-100 text-slate-600"}`}>{sel.status?.replace(/_/g, " ")}</span>
                </div>
                <p className="text-xs text-slate-500">{sel.email}{sel.crm_company_id ? " · linked to CRM" : ""}{sel.wants_app_account ? " · wants app account" : ""}</p>

                {sel.crm_duplicate && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="customer-duplicate-warning">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
                    <div className="text-xs text-amber-800">
                      <span className="font-semibold">Possible duplicate in CRM.</span> A company already exists:
                      <span className="font-medium"> {sel.crm_duplicate.name}</span>
                      {sel.crm_duplicate.vat_number ? ` (VAT ${sel.crm_duplicate.vat_number})` : ""}
                      {sel.crm_duplicate.company_type ? ` · ${sel.crm_duplicate.company_type}` : ""}.
                      Approving will update that record instead of creating a new one.
                    </div>
                  </div>
                )}

                {["company", "addresses", "operations_contact", "finance_contacts", "banking", "declarations"].map((k) => {
                  const v = sel[k];
                  if (!v || (typeof v === "object" && Object.keys(v).length === 0)) return null;
                  return (
                    <details key={k} className="mt-2 rounded-lg border border-slate-100" open={k === "company" || k === "operations_contact"}>
                      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700">{SECTION_LABELS[k] || prettify(k)}</summary>
                      {typeof v === "object" ? <KV obj={v} /> : <div className="px-3 pb-3 text-xs text-slate-600">{String(v)}</div>}
                    </details>
                  );
                })}

                <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                  <button data-testid="customer-approve-btn" disabled={busy || sel.status === "approved"}
                    onClick={() => { if (window.confirm(`Approve ${sel.company?.registered_name || sel.email}? This creates/updates the CRM company + contacts and notifies accounting.`)) act("approve", {}); }}
                    className="flex items-center gap-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    <CheckCircle2 size={15} /> Approve
                  </button>
                  <button data-testid="customer-info-btn" disabled={busy}
                    onClick={() => { const m = prompt("What information do you need from the customer?"); if (m) act("request-info", { message: m }); }}
                    className="flex items-center gap-1 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    <MessageSquare size={15} /> Request info
                  </button>
                  <button data-testid="customer-reject-btn" disabled={busy}
                    onClick={() => { const r = prompt("Rejection reason:"); if (r) act("reject", { reason: r }); }}
                    className="flex items-center gap-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    <XCircle size={15} /> Reject
                  </button>
                  <button data-testid="customer-merge-btn" disabled={busy}
                    onClick={() => { setMergeReason("Duplicate registration"); setMergeTarget(""); setMergeOpen(true); }}
                    className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50 hover:bg-slate-50">
                    <Copy size={15} /> Merge duplicate
                  </button>
                  <button data-testid="customer-cancel-btn" disabled={busy}
                    onClick={doCancel}
                    className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50 hover:bg-slate-50">
                    <Ban size={15} /> Cancel
                  </button>
                </div>

                {sel.merged_into && (
                  <p className="mt-2 text-xs text-slate-500">Merged into another record · {sel.cancellation_reason || ""}</p>
                )}
                {sel.status === "archived" && !sel.merged_into && sel.cancellation_reason && (
                  <p className="mt-2 text-xs text-slate-500">Cancelled — {sel.cancellation_reason}</p>
                )}

                {mergeOpen && (
                  <div className="mt-3 rounded-lg border border-slate-300 bg-slate-50 p-3" data-testid="customer-merge-panel">
                    <p className="mb-2 text-sm font-semibold text-slate-700">Merge this duplicate into an existing registration</p>
                    <p className="mb-2 text-xs text-slate-500">This record will be archived and linked to the record you choose below.</p>
                    <select data-testid="customer-merge-target" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}
                      className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                      <option value="">— Select the record to keep —</option>
                      {items.filter((it) => it.id !== sel.id).map((it) => (
                        <option key={it.id} value={it.id}>{(it.company?.registered_name || it.email)}{it.company?.vat_number ? ` · ${it.company.vat_number}` : ""} ({it.status?.replace(/_/g, " ")})</option>
                      ))}
                    </select>
                    <input data-testid="customer-merge-reason" value={mergeReason} onChange={(e) => setMergeReason(e.target.value)}
                      placeholder="Reason" className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                    <div className="flex gap-2">
                      <button data-testid="customer-merge-confirm" disabled={busy || !mergeTarget} onClick={doMerge}
                        className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">Confirm merge</button>
                      <button onClick={() => setMergeOpen(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600">Cancel</button>
                    </div>
                  </div>
                )}

                {sel.audit && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-xs font-semibold text-slate-400">Audit trail ({sel.audit.length})</summary>
                    <div className="mt-1 space-y-1">
                      {sel.audit.slice().reverse().map((a, i) => (
                        <div key={i} className="text-[11px] text-slate-500">{a.at?.slice(0, 16).replace("T", " ")} · {a.action} · {a.actor}{a.detail ? ` — ${a.detail}` : ""}</div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
